/* The bonus stage: a 2048 board whose tiles are this path's own steps. Two of
 * the same step compose into the step above — the thesis of the product, played
 * out. Nothing below the event tile is a decision, so the ladder stops there.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LADDER } from '../../data/ladder';
import { gLevel, type Dir } from '../../engine/game';
import { useGameStage } from '../../state/useGame';
import type { Block } from '../../types';

const PAD: Array<{ dir: Dir; cls: string; path: string }> = [
  { dir: 'up', cls: 'gu', path: 'M10 16V4M5 9l5-5 5 5' },
  { dir: 'left', cls: 'gl', path: 'M16 10H4M9 5l-5 5 5 5' },
  { dir: 'down', cls: 'gd', path: 'M10 4v12M5 11l5 5 5-5' },
  { dir: 'right', cls: 'gr', path: 'M4 10h12M11 5l5 5-5 5' },
];

const KEYS: Record<string, Dir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

const SWIPE_MIN = 24;

export function GameBlock({ block }: { block: Block }) {
  const { t } = useTranslation();
  const game = useGameStage(block);
  const boardRef = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);

  /* Arrows drive the board only while it holds focus, so the page still
     scrolls with the keyboard everywhere else. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest?.('.game')) return;
      const dir = KEYS[event.key];
      if (!dir) return;
      event.preventDefault();
      game.play(dir);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [game]);

  /* Swipe to play, on touch and with a mouse drag. */
  useEffect(() => {
    const onPointerUp = (event: PointerEvent) => {
      const start = swipe.current;
      swipe.current = null;
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) < SWIPE_MIN && Math.abs(dy) < SWIPE_MIN) return;
      game.play(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up');
    };
    document.addEventListener('pointerup', onPointerUp);
    return () => document.removeEventListener('pointerup', onPointerUp);
  }, [game]);

  const caption = game.caption ?? (game.over ? t('gameOver') : '');

  return (
    <div className="game" data-od-id={`game-${block.id}`}>
      <div className="g-top">
        <div className="g-stat">
          <div className="k">{t('score')}</div>
          <div className="v num" id="gscore">
            {game.score}
          </div>
        </div>
        <div className="g-stat">
          <div className="k">{t('best')}</div>
          <div className="v num" id="gbest">
            {game.best}
          </div>
        </div>
        <div className="g-stat">
          <div className="k">{t('moves')}</div>
          <div className="v num" id="gmoves">
            {game.moves}
          </div>
        </div>
      </div>

      <div className="g-play">
        <div
          className="g-board"
          id="gboard"
          role="group"
          tabIndex={0}
          aria-label={t('gameHow')}
          ref={boardRef}
          onPointerDown={(event) => {
            swipe.current = { x: event.clientX, y: event.clientY };
            boardRef.current?.focus({ preventScroll: true });
          }}
        >
          {game.board.map((value, index) => {
            const level = gLevel(value);
            return (
              <div
                key={index}
                className="g-cell"
                data-l={level || undefined}
                data-new={index === game.newTile ? 'true' : undefined}
              >
                {level > 0 && <span className="g-lbl">{t(LADDER[level - 1].nKey)}</span>}
              </div>
            );
          })}
        </div>

        <div className="g-side">
          <div className="g-pad" role="group" aria-label={t('moves')}>
            {PAD.map(({ dir, cls, path }) => (
              <button
                key={dir}
                type="button"
                className={`g${cls}`}
                data-act="g-move"
                data-dir={dir}
                aria-label={t(`d${dir.charAt(0).toUpperCase()}${dir.slice(1)}`)}
                onClick={() => game.play(dir)}
              >
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d={path} />
                </svg>
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" data-act="g-reset" onClick={game.reset}>
            {t('gameRestart')}
          </button>
        </div>
      </div>

      <p className="g-cap" id="gcap" aria-live="polite">
        {caption}
      </p>

      <div className="stack">
        <p className="eyebrow">{t('ladderTitle')}</p>
        <div className="ladder" id="gladder">
          {LADDER.map((rung, i) => (
            <span key={rung.fKey} className="rung" data-reached={String(game.peak >= i + 1)}>
              <i />
              {t(rung.fKey)}
            </span>
          ))}
        </div>
        <p className="meta">
          {t('gameGoal')} {t('gameHow')}
        </p>
      </div>
    </div>
  );
}
